import { FC, useMemo, useState } from 'react';
import Modal from '@new/common/ui/Modal/Modal';
import {
  useGetAvailableCoursesQuery,
  useGetUserCoursesQuery,
  useGetUserQuery,
  useAddCourseMutation,
} from '@teachur/http-client';
import { toast } from 'react-toastify';
import { useQueryClient } from '@tanstack/react-query';
import { teachurKeys } from '@teachur/http-client';
import { AddCourseSearch } from './AddCourseSearch';
import { AddCourseList } from './AddCourseList';
import { getErrorMessage } from '@new/lib';
import Information from '@new/common/ui/Info/Info';
import { isMasterStudent } from '@new/features/degree/utils/isMasterStudent';

interface AddCourseModalProps {
  isOpen: boolean;
  onClose: () => void;
  userId: number;
}

export const AddCourseModal: FC<AddCourseModalProps> = ({ isOpen, onClose, userId }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const queryClient = useQueryClient();

  const { data: availableCoursesData } = useGetAvailableCoursesQuery({
    searchText: searchQuery,
    pageSize: 100,
  });
  const { data: userCoursesData } = useGetUserCoursesQuery(userId);
  const { data: targetUser } = useGetUserQuery(userId);
  const { mutate, isPending } = useAddCourseMutation({
    onSuccess: () => {
      toast.success('Course added successfully!');
      queryClient.invalidateQueries({ queryKey: teachurKeys.courses.list() });
      queryClient.invalidateQueries({ queryKey: teachurKeys.users.userCourses(userId) });
    },
    onError: (error: unknown) => toast.error(getErrorMessage(error, 'Failed to add course')),
  });

  const canEnrollMasterCourses = isMasterStudent(targetUser?.enrolledDegrees);
  const availableCourses = useMemo(() => {
    const all = availableCoursesData?.data || [];
    return canEnrollMasterCourses ? all : all.filter((c) => c.level !== 'master');
  }, [availableCoursesData, canEnrollMasterCourses]);
  const addedCourseIds = new Set((userCoursesData ?? []).map((uc) => uc.courseId));

  const onAdd = (courseId: number) => {
    if (isPending) return;
    mutate({ courseId, userId });
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Add new courses"
      className="w-200 max-w-sm lg:max-w-3xl text-(--neutral-black-NB1100)! max-h-171.5 md:h-175 lg:h-205.5"
    >
      <div className="flex flex-col gap-3 text-(--neutral-black-NB1100)">
        <Information text="You can add more courses." />
        <AddCourseSearch searchQuery={searchQuery} onSearchChange={setSearchQuery} />
        <AddCourseList courses={availableCourses} addedCourseIds={addedCourseIds} onAdd={onAdd} />
      </div>
    </Modal>
  );
};
